import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Select, Space, Tooltip, Typography } from "antd";

import { updatePatientFacility } from "../../../api/api";
import { selectPatientDetails, selectProvidersFacility } from "../../../store/selector";
import ErrorMessage from "../../customComponent/customMessages/ErrorMessage";
import SuccessMessage from "../../customComponent/customMessages/SuccessMessage";
import ACTIONS from "../../../store/action";
import { fontWeights, sizes } from "../../../constant/styles";
import { GRAY_400, TEXT4, WHITE } from "../../../constant/colors";

const { Option } = Select;
const { Text } = Typography;

const PatientFacility = ({
  selectStyle = {},
  secondaryTextStyle = { fontSize: sizes.small, color: TEXT4, fontWeight: fontWeights.semibold }
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isSelectHover, setIsSelectHover] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [patientFacilities, setPatientFacilities] = useState([]);
  const { patientId } = useParams();

  const facilities = useSelector(selectProvidersFacility);
  const patientDetails = useSelector(selectPatientDetails);

  useEffect(() => {
    if (!facilities || !facilities.length) return;

    if (!patientDetails?.facilities?.length) {
      setPatientFacilities([]);
      return;
    }

    const patientFacilities = patientDetails.facilities
      .map((facility) => {
        const chosenFacility = facilities.find(({ id }) => id && id === facility.id);
        if (chosenFacility) return chosenFacility;
      })
      .filter((item) => item)
      .sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

    setPatientFacilities(patientFacilities);
  }, [facilities, patientDetails]);

  const updateFacilityHandler = async (values) => {
    try {
      setIsFetching(true);
      const newFacilities = values
        .map((value) => {
          const selectedFacility = facilities.find((facility) => facility.id === value);
          return selectedFacility;
        })
        .filter((f) => f);

      const updateFacilityRes = await updatePatientFacility(patientId, values);

      if (updateFacilityRes && updateFacilityRes.data && updateFacilityRes.data.success) {
        SuccessMessage(t("successMessages.successfully_updated_facility"));
        setPatientFacilities(newFacilities);

        // Update patient details with new facilities and single facility fields (for claims/pharmacy)
        const updatedPatientDetails = {
          ...patientDetails,
          facilities: newFacilities.map((f) => ({ id: f.id, name: f.name })),
          facility: updateFacilityRes.data.facility,
          facilityId: updateFacilityRes.data.facilityId
        };

        dispatch(ACTIONS.updatePatientDetailsAction(updatedPatientDetails));
      } else {
        ErrorMessage(t("errorMessages.failed_to_update_facility"));
      }
    } catch (error) {
      ErrorMessage(t("errorMessages.failed_to_update_facility"));
    } finally {
      setIsFetching(false);
    }
  };

  const facilitiesIds = patientFacilities?.map(({ id }) => id) || [];
  const facilitiesTextToDisplay = patientFacilities?.map((facility) => facility.name).join(" | ") || "";

  const compareFacilities = (a, b) => {
    const aIsSelected = facilitiesIds.indexOf(a.value) > -1;
    const bIsSelected = facilitiesIds.indexOf(b.value) > -1;
    if ((aIsSelected && bIsSelected) || (!aIsSelected && !bIsSelected)) {
      return a.children.localeCompare(b.children);
    }
    if (aIsSelected && !bIsSelected) {
      return -1;
    }
    if (!aIsSelected && bIsSelected) {
      return 1;
    }
  };

  return (
    <Space direction="vertical" size={0} style={{ width: "100%" }}>
      <Text style={{ ...secondaryTextStyle, paddingLeft: 12 }}>{t("patientTopSection.facilities")}</Text>
      <Tooltip
        open={!!facilitiesTextToDisplay.length && !isSelectOpen && isSelectHover}
        title={<Text>{facilitiesTextToDisplay}</Text>}
        color={WHITE}
      >
        <div id="patient_header_facilities">
          <Select
            mode="multiple"
            showArrow={isSelectHover || isFetching}
            allowClear={false}
            loading={isFetching}
            bordered={false}
            maxTagCount={1}
            filterOption={(inputValue, option) => {
              return option.children?.toLowerCase()?.includes(inputValue?.toLowerCase());
            }}
            onDropdownVisibleChange={(open) => setIsSelectOpen(open)}
            filterSort={compareFacilities}
            maxTagTextLength={patientFacilities.length > 1 ? 8 : 16}
            dropdownMatchSelectWidth={false}
            onChange={updateFacilityHandler}
            placeholder={<Text style={{ color: GRAY_400, fontWeight: 400 }}>{t("no_facilities")}</Text>}
            value={facilitiesIds}
            getPopupContainer={(trigger) => trigger.parentNode}
            onMouseEnter={() => setIsSelectHover(true)}
            onMouseLeave={() => setIsSelectHover(false)}
            style={selectStyle}
          >
            {facilities &&
              facilities.map((facility, index) => (
                <Option key={index} value={facility.id} disabled={isFetching}>
                  {facility.name}
                </Option>
              ))}
          </Select>
        </div>
      </Tooltip>
    </Space>
  );
};

export { PatientFacility };
